<template>

  <teleport to="body">
        <div v-if="showNotification" class="favorite-notification">
          <img src="/success.png" alt="Success" class="notification-icon" />
          <span>{{ notificationMessage }}</span>
        </div>
  </teleport>

  <div class="feed-container" :class="{ dark: theme === 'dark' }">
    <FeedHeader
      class="feed-header"
      @show-employer-tutorial="handleShowEmployerTutorial"
      @employer-status="handleEmployerStatus"
      @pending-company-level="handlePendingCompanyLevel"
      @renew-subscription-status="handleRenewSubscriptionStatus"
    />

    <div class="feed-layout">

    <div ref="filtersPanel" class="filters-panel">
      <div ref="filtersInner" class="filters-inner">
        <FeedFilters @filtersChanged="onFiltersChanged" />
      </div>
    </div>

      <div ref="feedContent" class="feed-content">
        <div class="search-wrapper">
            <SearchBar
              v-model="searchText"
              :activeQuery="activeQuery"
              :city="currentCity?.name || 'Москва'"
              @clearQuery="clearSearch"
              @openRegionSelection="handleOpenRegionSelection"
              @performSearch="performSearch"
            />
            <button
              class="search-button"
              @click="performSearch"
              :disabled="!searchText.trim()"
            >
              <img src="../assets/search_dark.png" alt="Search" />
              Поиск
            </button>
        </div>

        <div class="vacancy-count-wrapper">
          <div v-if="isLoading" class="vacancy-count-skeleton shimmer">
            <span class="number-placeholder"></span>&nbsp;&nbsp;всего вакансий
          </div>

          <div v-else-if="totalCount !== null" class="vacancy-count">
            <strong>{{ totalCount }}</strong> вакансии найдено в городе
            <span v-if="currentCity?.name"> {{ currentCity.name }}</span>
          </div>

          <div
            class="create-vacancy-control"
            :class="{ disabled: !isEmployer }"
          >
            <button
              ref="createVacancyButtonRef"
              class="add-button"
              @click="handleCreateVacancy"
              :disabled="!isEmployer || isCreateRedirecting"
            >
              <template v-if="!isCreateRedirecting">
                <img
                  src="../assets/add-icon.png"
                  alt="+"
                />
              </template>

              <template v-else>
                <img
                  src="/spinner.png"
                  class="spinner-icon"
                />
              </template>
            </button>

            <div
              v-if="!isEmployer"
              class="create-vacancy-hint"
            >
              <div class="create-vacancy-hint-title">
                Хотите размещать вакансии?
              </div>

              <div class="create-vacancy-hint-text">
                Перейдите в
                <strong>Профиль → Размещение контента</strong>
                и включите размещение вакансий.
              </div>
            </div>
          </div>
        </div>

        <template v-if="!isLoading">
          <div class="job-grid">
            <VacancyItem
              v-for="job in jobs"
              :key="job.id"
              :vacancy="job"
              @open-vacancy="goToVacancy"
              @open-our-vacancy="goToOurVacancy"
              @toggle-favorite="handleFavorite"
            />
          </div>

          <div v-if="isFetchingMore" class="pagination-skeleton-row">
            <SkeletonJobCard v-for="n in 3" :key="`pagination-skeleton-${n}`" />
          </div>

        </template>

        <div v-else class="skeleton-container">
          <SkeletonJobCard v-for="n in 6" :key="n" />
        </div>

        <div v-if="!isLoading && jobs.length === 0" class="empty-state">
          <img src="../assets/empty-vacancies.png" alt="Not found" class="empty-icon" />
          <p class="empty-message">К сожалению по вашему запросу<br />ничего не найдено</p>
        </div>

      </div>
      
    </div>
    <Footer v-if="!isMobile" class="feed-footer" />
    <MobileTabBar v-else />

      <teleport to="body">
        <SettingsDropdown
          v-if="isDropdownVisible"
          :style="{ top: dropdownPosition.top + 'px', left: dropdownPosition.left + 'px' }"
        />
    </teleport>

    <CreateVacancyTutorialOverlay
      :visible="showCreateVacancyTutorial"
      :target-element="createVacancyButtonRef"
      @close="showCreateVacancyTutorial = false"
      @create="showCreateVacancyTutorial = false"
    />

    <teleport to="body">
      <Paywall v-if="showPaywall" @close="showPaywall = false" />
    </teleport>
    
    <ProfileRegionDrawer
      :visible="showRegionDrawer"
      @update:modelValue="handleCityUpdated"
      @close="handleCloseRegionDrawer"
    />

    <Snackbar
      :message="snackbarMessage"
      :type="snackbarType"
      :show="snackbarVisible"
    />

    <ProfileCompanyDrawer
      :visible="showCompanyDrawer"
      @close="showCompanyDrawer = false"
      @company-added="onCompanyAdded"
    />

    <CompanyRoleSelectionModal
      :visible="showCompanyRoleModal"
      :company-employee="pendingCompanyEmployee"
      :is-saving="isSavingCompanyLevel"
      @select="handleCompanyRoleSelected"
      @close="closeCompanyRoleModal"
    />

  <RenewSubscriptionDrawer
    v-model="showRenewDrawer"
    @open-paywall="openPremiumPaywall"
  />

  </div>
    
</template>

<script setup>

import { ref, onMounted, onUnmounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import { AnalyticsService, AnalyticsEvent } from "../utils/AnalyticsService";

import PendingCompanyLevelService from "../api/PendingCompanyLevelService";
import CompanyRoleSelectionModal from "../components/CompanyRoleSelectionModal.vue";
import MobileTabBar from "../components/MobileTabBar2.vue";
import ProfileCompanyDrawer from '../components/profile/ProfileCompanyDrawer.vue';
import Paywall from "../views/Paywall.vue";
import FeedHeader from "../components/FeedHeader.vue";
import Footer from "../components/MiniFooter.vue";
import SkeletonJobCard from "../components/SkeletonJobCard.vue";
import VacancyItem from "../components/VacancyItem.vue";
import VacancyService from "../api/VacancyService.js";
import SearchBar from "../components/SearchBar.vue";
import SearchService from "../api/SearchService.js";
import SettingsDropdown from "../components/SettingsDropdown.vue";
import FeedFilters from "../components/FeedFilters.vue";
import ProfileRegionDrawer from '../components/profile/ProfileRegionDrawer.vue';
import Snackbar from "../components/Snackbar.vue";
import FavoritesService from "../api/FavoritesService";
import PremiumService from "../api/PremiumService.js";
import CreateVacancyTutorialOverlay from "../components/CreateVacancyTutorialOverlay.vue";
import RenewSubscriptionDrawer from "../components/RenewSubscriptionDrawer.vue";

const filtersInner = ref(null);
const router = useRouter();
const feedContent = ref(null); 
const theme = ref(localStorage.getItem("theme") || "dark");
const showRegionDrawer = ref(false);
const currentCity = ref({ name: "Москва" });

const snackbarMessage = ref("");
const snackbarType = ref("success");
const snackbarVisible = ref(false);
const searchText = ref("");
const activeQuery = ref('');
const isEmployer = ref(false);

const isLoading = ref(true);
const jobs = ref([]);
const hasMoreJobs = ref(true);
const showNotification = ref(false);
const notificationMessage = ref("");
const isFetchingMore = ref(false);
const isDropdownVisible = ref(false);
const dropdownPosition = ref({ top: 0, left: 0 });
const showPaywall = ref(false)
const totalCount = ref(null);
const selectedSpecifics = ref([]);
const selectedRemote = ref("all");
const isCreateRedirecting = ref(false);
const showCompanyDrawer = ref(false);
const selectedRegion = ref('all');
const showRenewDrawer = ref(false);

const isMobile = ref(window.innerWidth <= 768);

const pendingCompanyEmployee = ref(null);
const showCompanyRoleModal = ref(false);
const isSavingCompanyLevel = ref(false);

const checkIsMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};
const onCompanyAdded = (payload) => {
  console.log("✅ Компания добавлена:", payload);
  router.push('/create-vacancy');
};

const updateFiltersMargin = () => {
  const scrollTop = feedContent.value?.scrollTop || 0;
  if (filtersInner.value) {
    filtersInner.value.style.marginTop = scrollTop > 30 ? '10px' : '150px';
  }
};

const handleRenewSubscriptionStatus = (value) => {
  showRenewDrawer.value = value === true;
};

function showSnackbar(message, type = "success") {
  snackbarMessage.value = message;
  snackbarType.value = type;
  snackbarVisible.value = false; 
  nextTick(() => {
    snackbarVisible.value = true;
  });
}

const handleCityUpdated = (city) => {
  const normalizedCity = {
    id: city._id,
    name: city.city,
    region_id: city.region_id,
    region_name: city.region_name
  };

  localStorage.setItem("selected_city", JSON.stringify(normalizedCity));
  currentCity.value = normalizedCity;

  showSnackbar(`Город обновлён на ${normalizedCity.name}`, "success");
};

const handleCloseRegionDrawer = () => {
  showRegionDrawer.value = false;
};

const getCityId = () => {
  try {
    const savedCity = localStorage.getItem("selected_city");
    if (!savedCity) return "msk";

    const parsed = JSON.parse(savedCity);
    return parsed?.id || "msk";
  } catch (e) {
    console.warn("❌ Ошибка чтения города:", e);
    return "msk";
  }
};

const createVacancyButtonRef = ref(null);
const showCreateVacancyTutorial = ref(false);

const onFiltersChanged = (filters) => {
  selectedSpecifics.value = filters.specifics || [];
  selectedRemote.value = filters.remote || 'all';
  selectedRegion.value = filters.region || 'all'; 
  resetAndFetch();
};

const resetAndFetch = async () => {
  console.log("🚀 resetAndFetch старт");

  isLoading.value = true;
  jobs.value = [];
  VacancyService.vacancies = [];
  VacancyService.nextId = null;

  debugFetch("resetAndFetch");

  const result = await VacancyService.fetchVacancies(
    12,
    selectedSpecifics.value,
    selectedRemote.value,
    getCityId(),
    selectedRegion.value
  );

  console.log("📦 RAW RESULT:", result);

  if (result?.vacancies?.length) {
    result.vacancies.forEach(job => {
      console.log("🧾 Vacancy:", {
        id: job._id,
        title: job.title,
        is_liked: job.is_liked
      });
    });
  } else {
    console.log("⚠️ Вакансии пустые или undefined");
  }

  jobs.value = [...result.vacancies];

  console.log("✅ jobs.value после установки:", jobs.value.map(j => ({
    id: j._id,
    is_liked: j.is_liked
  })));

  totalCount.value = result.total_count ?? null;
  hasMoreJobs.value = result.vacancies.length === 12;
  isLoading.value = false;

  console.log("🏁 resetAndFetch завершён");
};

const handleScroll = async () => {
  const el = feedContent.value;
  if (!el) return;

  const { scrollTop, scrollHeight, clientHeight } = el;
  const nearBottom = scrollTop + clientHeight >= scrollHeight - 100;

  if (nearBottom && !isLoading.value && hasMoreJobs.value && !isFetchingMore.value) {
    await fetchJobs();
  }
};

onMounted(async () => {

  const savedCity = localStorage.getItem("selected_city");

  if (savedCity) {
    try {
      currentCity.value = JSON.parse(savedCity);
    } catch (e) {
      console.warn("❗ Ошибка парсинга города:", e);
      currentCity.value = { name: "Москва", id: "msk" }; 
    }
  } else {
    currentCity.value = { name: "Москва", id: "msk" };
  }

  document.documentElement.setAttribute("data-theme", theme.value);

  await resetAndFetch();

  if (feedContent.value) {
    feedContent.value.addEventListener("scroll", handleScroll);
    feedContent.value.addEventListener("scroll", updateFiltersMargin);
  }
});

onUnmounted(() => {
    if (feedContent.value) {
    feedContent.value.removeEventListener('scroll', updateFiltersMargin);
  }

  if (feedContent.value) {
    feedContent.value.removeEventListener("scroll", handleScroll);
  }
});

const getVacancyId = vacancy => {
  return (
    vacancy?._id ||
    vacancy?.id ||
    vacancy?.vacancy_id ||
    vacancy?.vacancyID
  );
};

const goToVacancy = vacancy => {
  const vacancyId = getVacancyId(vacancy);

  if (!vacancyId) {
    console.error(
      "❌ Нет ID у вакансии:",
      vacancy
    );

    return;
  }

  router.push({
    path: `/vacancy/${vacancyId}`
  });
};

const handleEmployerStatus =
  (value) => {

    isEmployer.value =
      value;

    console.log(
      "👔 isEmployer из FeedHeader:",
      value
    );

};

const goToOurVacancy = vacancy => {
  const vacancyId = getVacancyId(vacancy);

  if (!vacancyId) {
    console.error(
      "❌ Нет ID у нашей вакансии:",
      vacancy
    );

    return;
  }

  console.log(
    "🏢 Открываем нашу вакансию:",
    vacancyId
  );

  router.push({
    path: `/edit-my-vacancy/${vacancyId}`
  });
};

const handleFavorite = async (payload) => {
  const done =
    typeof payload === "object"
      ? payload?.done
      : null;

  try {
    console.log("⭐ handleFavorite вызван");
    console.log("📦 payload:", payload);

    const vacancyId =
      typeof payload === "string"
        ? payload
        : payload?.vacancyId;

    console.log("🆔 vacancyId:", vacancyId);

    if (!vacancyId) {
      console.warn("❌ Нет vacancyId");
      return;
    }

    console.log("📡 Отправляем запрос в toggleFavorite...");

    const result =
      await FavoritesService.toggleFavorite(
        vacancyId
      );

    console.log(
      "📩 Ответ от сервера:",
      result
    );

    const job =
      jobs.value.find(
        j => j._id === vacancyId
      );

    console.log(
      "🔎 Найден job:",
      job
    );

    if (job) {
      console.log(
        "🛠 Обновляем job.is_liked:",
        result.is_favorite
      );

      job.is_liked =
        result.is_favorite;
    } else {
      console.warn(
        "⚠️ Вакансия не найдена в jobs"
      );
    }

    notificationMessage.value =
      result.is_favorite
        ? "Вакансия добавлена в избранное"
        : "Удалено из избранного";

    console.log(
      "🔔 Показываем уведомление:",
      notificationMessage.value
    );

    showNotification.value = true;

    setTimeout(() => {
      showNotification.value = false;

      console.log(
        "🔕 Уведомление скрыто"
      );
    }, 1500);

  } catch (error) {

    console.error(
      "❌ Ошибка в handleFavorite:",
      error
    );

  } finally {

    done?.();

  }
};

const debugFetch = (label) => {
  console.log(`📡 [${label}] fetchVacancies вызов`);
  console.log("👉 selectedSpecifics:", selectedSpecifics.value);
  console.log("👉 selectedRemote:", selectedRemote.value);
  console.log("👉 currentCity:", currentCity.value);
  console.log("👉 localStorage:", localStorage.getItem("selected_city"));
  console.log("👉 getCityId():", getCityId());
};

const fetchJobs = async () => {
  if (!hasMoreJobs.value || isFetchingMore.value) return;

  console.log("🚀 fetchJobs вызван");
  console.log("📍 Фильтры:", {
    specifics: selectedSpecifics.value,
    remote: selectedRemote.value,
    city: currentCity.value?.id
  });

  if (isLoading.value) {
    console.log("🟡 Первый запрос (isLoading)");

    debugFetch("fetchJobs FIRST");

    const result = await VacancyService.fetchVacancies(
      12,
      selectedSpecifics.value,
      selectedRemote.value,
      getCityId(),
      selectedRegion.value
    );

    console.log("📦 RAW result:", result);

    const newJobs = result.vacancies || [];

    console.log("📋 Получено вакансий:", newJobs.length);

    newJobs.forEach(job => {
      console.log("🧾 Vacancy:", {
        id: job._id,
        title: job.title,
        is_liked: job.is_liked
      });
    });

    totalCount.value = result.total_count ?? null;
    jobs.value = [...jobs.value, ...newJobs];
    hasMoreJobs.value = newJobs.length === 12;
    isLoading.value = false;

  } else {
    console.log("🔵 Подгрузка следующей страницы");

    isFetchingMore.value = true;

    debugFetch("fetchJobs NEXT");

    const result = await VacancyService.fetchVacancies(
      12,
      selectedSpecifics.value,
      selectedRemote.value,
      getCityId(),
      selectedRegion.value
    );

    console.log("📦 RAW result:", result);

    const newJobs = result.vacancies || [];

    console.log("📋 Получено вакансий:", newJobs.length);

    newJobs.forEach(job => {
      console.log("🧾 Vacancy:", {
        id: job._id,
        title: job.title,
        is_liked: job.is_liked
      });
    });

    totalCount.value = result.total_count ?? null;
    jobs.value = [...jobs.value, ...newJobs];
    hasMoreJobs.value = newJobs.length === 12;
    isFetchingMore.value = false;
  }

  console.log("⭐ Вакансии с is_liked === true:");
  jobs.value
    .filter(j => j.is_liked === true)
    .forEach(j => {
      console.log("❤️", j._id, j.title);
    });
};

const performSearch = async () => {
  activeQuery.value = searchText.value;

  isLoading.value = true;
  jobs.value = [];
  hasMoreJobs.value = false;
  VacancyService.vacancies = [];
  VacancyService.nextId = null;

  const results = await SearchService.searchVacancies(searchText.value);

  jobs.value = [...results];
  totalCount.value = results.length;
  isLoading.value = false;
  searchText.value = '';
};

const clearSearch = async () => {
  activeQuery.value = '';
  searchText.value = '';

  isLoading.value = true;
  jobs.value = [];
  hasMoreJobs.value = false;
  VacancyService.vacancies = [];
  VacancyService.nextId = null;

  const result = await VacancyService.fetchVacancies(
    12,
    selectedSpecifics.value,
    selectedRemote.value,
    getCityId()
  );

  AnalyticsService.log(AnalyticsEvent.SEARCH_VACANCY);

  jobs.value = [...result.vacancies];
  totalCount.value = result.total_count ?? null;
  hasMoreJobs.value = result.vacancies.length === 12;
  isLoading.value = false;
};

const handleShowEmployerTutorial = async () => {
  console.log(
    "🎓 Показываем tutorial создания вакансии"
  );

  await nextTick();

  console.log(
    "🎯 Target кнопки:",
    createVacancyButtonRef.value
  );

  if (!createVacancyButtonRef.value) {
    console.warn(
      "⚠️ Кнопка создания вакансии ещё не найдена"
    );
    return;
  }

  showCreateVacancyTutorial.value = true;
};

const handleOpenRegionSelection = () => {
  console.log("🌍 Открываем шторку выбора города");
  showRegionDrawer.value = true;
};

const handleCreateVacancy = async () => {

  if (!isEmployer.value) {
    console.log(
      "🚫 Создание вакансий доступно только работодателям"
    );

    return;
  }

  if (isCreateRedirecting.value) {
    return;
  }

  if (isCreateRedirecting.value) {
    return;
  }

  isCreateRedirecting.value = true;

  /*
    Перед новой проверкой закрываем предыдущие окна,
    чтобы они случайно не накладывались друг на друга.
  */
  showPaywall.value = false;
  showCompanyDrawer.value = false;

  try {
    console.log(
      "🚀 Проверяем возможность создания вакансии"
    );

    const premium =
      await PremiumService.fetchMySubscription();

    if (!premium) {
      throw new Error(
        "Сервер не вернул настройки подписки"
      );
    }

    console.log(
      "📦 Получены ограничения:",
      premium.toJSON()
    );

    /*
      1. Сначала проверяем наличие компании.

      Если компании нет, Premium paywall пока
      показывать не нужно — пользователю сначала
      необходимо создать компанию.
    */
    if (premium.hasNoCompanies) {
      console.log(
        "🏢 У пользователя нет компании — открываем drawer"
      );

      showCompanyDrawer.value = true;
      return;
    }

    /*
      2. Решение о показе paywall принимает сервер.

      На клиенте больше не считаем количество
      вакансий и не проверяем тариф самостоятельно.
    */
    if (premium.shouldShowVacancyPaywall) {
      console.log(
        "💳 Сервер запросил paywall для создания вакансии"
      );

      AnalyticsService.log(
        AnalyticsEvent.OPEN_PAYWALL
      );

      showPaywall.value = true;
      return;
    }

    if (premium.hasMultipleCompanies) {
      console.log(
        "🏢 У пользователя несколько компаний"
      );

      await router.push({
        path: "/create-vacancy",
        query: {
          "is-multiple-companies": "true"
        }
      });

      return;
    }

    console.log(
      "✅ Создание вакансии разрешено"
    );

    await router.push("/create-vacancy");
  } catch (error) {
    console.error(
      "❌ Ошибка проверки возможности создания вакансии:",
      error.response?.data ||
      error.message ||
      error
    );
    showSnackbar(
      "Не удалось проверить подписку. Попробуйте ещё раз",
      "error"
    );
  } finally {
    isCreateRedirecting.value = false;
  }
};

function handlePendingCompanyLevel(companyEmployee) {
  if (!companyEmployee) return;

  pendingCompanyEmployee.value = companyEmployee;
  showCompanyRoleModal.value = true;
}

// ============================================================
// COMPANY ROLE SELECTION
// ============================================================

const handleCompanyRoleSelected = async (payload) => {

if (isSavingCompanyLevel.value) {
  return;
}

try {

  isSavingCompanyLevel.value = true;

  // ========================================================
  // UPDATE COMPANY EMPLOYEE LEVEL
  // ========================================================

  const result =
    await PendingCompanyLevelService.setCompanyEmployeeLevel(
      payload.relationId,
      payload.level
    );

  console.log(
    "✅ Роль успешно сохранена:",
    result.companyEmployee
  );

  // ========================================================
  // CLOSE MODAL
  // ========================================================

  showCompanyRoleModal.value = false;

  pendingCompanyEmployee.value = null;

} catch (error) {

  console.error(
    "❌ Не удалось сохранить роль:",
    error
  );

  // При ошибке оставляем окно открытым,
  // чтобы пользователь мог попробовать ещё раз.

} finally {

  isSavingCompanyLevel.value = false;

}
};

// Закрытие.
function closeCompanyRoleModal() {
  if (isSavingCompanyLevel.value) return;

  showCompanyRoleModal.value = false;
}


const openPremiumPaywall = () => {
  console.log(
    "💎 Открываем Paywall для продления подписки"
  );

  // Закрываем уведомление о продлении
  showRenewDrawer.value = false;

  // Закрываем другие окна, чтобы они не перекрывали Paywall
  showCompanyDrawer.value = false;
  showCompanyRoleModal.value = false;

  // Аналитика
  AnalyticsService.log(
    AnalyticsEvent.OPEN_PAYWALL
  );

  // Открываем существующий Paywall
  showPaywall.value = true;
};


</script>

<style scoped>

.feed-container {
  width: 100%;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background-color: var(--background-color);
  overflow: visible;
}

html {
  scrollbar-gutter: stable both-edges; 
}

.feed-content::-webkit-scrollbar {
  display: none;
}

.feed-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  width: 94%; 
  width: 1188px;
  margin: 0 auto;
  padding: 20px 0;
  padding-left: 10px;
  background-color: var(--background-color);
}

.feed-footer {
  flex-shrink: 0;
  width: 100%;
}

.job-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr); 
  grid-row-gap: 20px; 
  justify-content: center;
  padding-bottom: 20px;
}

.load-more {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 12px 20px;
  font-size: 12px;
  font-family: "Eastman-Bold", sans-serif;
  border-radius: 28px;
  cursor: pointer;
  transition: opacity 0.3s;
  margin: 30px auto;
  display: block;
}

.load-more:hover {
  opacity: 0.8;
}

body, html {
  overflow-x: hidden;
}

.favorite-notification {
  position: fixed;
  top: 15%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  font-family: "Eastman-Medium", sans-serif;
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.3);
  opacity: 0.9;
  animation: bounce 0.5s ease-in-out;
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  z-index: 9999; 
  pointer-events: none; 
  background: var(--notification-bg);
  color: var(--notification-text);
  backdrop-filter: blur(10px);
  z-index: 9999;
  pointer-events: none;
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}

.favorite-notification::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: inherit;
  backdrop-filter: blur(1190px);
  border-radius: inherit;
  z-index: 99999; 
}

.notification-icon {
  width: 18px;
  height: 18px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease-in-out, transform 0.5s ease-in-out;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -55%);
}

@keyframes bounce {
  0% { transform: translate(-50%, -60%); opacity: 0; }
  50% { transform: translate(-50%, -50%); opacity: 1; }
  100% { transform: translate(-50%, -50%); opacity: 0.9; }
}

:root {
  --notification-bg: black;
  --notification-text: white;
}

[data-theme="dark"] {
  --notification-bg: rgba(22, 22, 22, 0.85);
  --notification-text: white;
}

[data-theme="light"] {
  --notification-bg: rgba(255, 255, 255, 0.85);
  --notification-text: black;
}

.search-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
  margin-left: -200px;
}

.search-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(to right, #2173E5, #204F90);
  color: white;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  padding: 10px 20px;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.search-button:hover {
  opacity: 0.9;
  transform: scale(1.02);
}

.search-button img {
  width: 12px;
  height: 12px;
  object-fit: contain;
}

.bottom-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
}

.spinner-icon {
  width: 28px;
  height: 28px;
  animation: spin 1s linear infinite;
}

.settings-dropdown {
  position: fixed;
  background: var(--card-bg);
  border-radius: 12px;
  padding: 10px;
  box-shadow: 0px 4px 20px rgba(0,0,0,0.3);
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.skeleton-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 20px;   
  row-gap: 24px;     
  justify-content: center;
  padding: 20px 20px;     
}
.feed-layout {
  display: flex;
  flex-direction: row;
  height: 100%;
  overflow: hidden;
  padding: 0 10px;
  max-width: 1720px;
  margin: 0 auto;
  gap: 0px;
}

@media screen and (min-width: 1441px) {
  .feed-layout {
    gap: 10px;
  }
}

.filters-panel {
  margin-left: -10px;
  width: 250px;
  flex-shrink: 0;
  position: sticky;
  top: 100px; 
  align-self: flex-start;
  height: calc(100vh - 120px);
  overflow-y: auto;
  scrollbar-width: none;
}

.filters-panel {
  transition: margin-top 0.3s ease;
}

.filters-inner {
  margin-top: 150px;
  transition: margin-top 0.3s ease;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 80px;
  margin-left: -200px;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  margin-bottom: 12px;
}

.empty-message {
  font-size: 14px;
  font-family: "Eastman-Regular", sans-serif;
  color: #4e4e4e;
  line-height: 1.5;
}

.vacancy-count {
  text-align: center;
  width: fit-content;
  margin-left: 300px;
  margin-bottom: 30px;
  margin-top: 30px;
  letter-spacing: -0.3px;
  font-size: 15px;
  min-width: 350px; 
  white-space: nowrap;
}

.vacancy-count strong {
  font-weight: 600;
  font-size: 16px;
}

.filters-panel::-webkit-scrollbar {
  width: 6px;
}
.filters-panel::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
}

.feed-header {
 height: 94px;
}

.vacancy-count-skeleton {
  text-align: center;
  width: fit-content;
  margin-left: 300px;
  margin-bottom: 30px;
  margin-top: 30px;
  font-size: 16px;
  font-family: "Eastman-Regular", sans-serif;
  display: flex;
  align-items: center;
  gap: 4px;
}

.number-placeholder {
  display: inline-block;
  width: 30px;
  height: 16px;
  border-radius: 8px;
  background: #ccc;
  position: relative;
  overflow: hidden;
}

.shimmer::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.4) 50%,
    rgba(255,255,255,0) 100%
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  100% {
    left: 100%;
  }
}

.vacancy-count-wrapper {
  display: flex;
  align-items: center;
  gap: 380px;
  margin-left: 0px;
  margin-bottom: 0px;
  margin-top: 0px;
  height: 60px;
}

.add-button img {
  width: 24px;
  height: 24px;
  margin-left: 100px;
}

.add-button {
  background: transparent;
  border: none;
  padding: 1px 12px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  cursor: pointer;
}

.add-button:hover {
  opacity: 0.9;
  transform: scale(1.05);
}

.search-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none !important;
}

html {
  scrollbar-gutter: stable both-edges;
}

@media (max-width: 430px) {

  .filters-panel {
    display: none;
  }

  .feed-layout {
    padding: 0;
  }

  .feed-content {
    width: 100%;
    padding: 10px;
  }

  .search-wrapper {
    width: 100%;
    margin-left: 0;
    padding: 0 6px;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  .search-button {
  width: 44px;
  height: 44px;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  }

  .search-button img {
  width: 16px;
  height: 16px;
  margin-left: 10px;
  }

  .search-button {
  font-size: 0;
  }

  /* вакансии в 1 колонку */
  .job-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  /* счетчик вакансий */
  .vacancy-count,
  .vacancy-count-skeleton {
    margin-left: 0;
    text-align: center;
    min-width: auto;
  }

  .add-button img {
    margin-left: 0;
  }

  .empty-state {
    margin-left: 0;
  }

  .vacancy-count-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  margin: 10px 0 20px 0;
  height: 44px;
  }

  .vacancy-count,
  .vacancy-count-skeleton {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    margin: 0;
    text-align: center;
    font-size: 13px;
    line-height: 1.3;
    width: max-content;
  }
  .vacancy-count {
  min-width: auto;
  white-space: normal;
  }

  .skeleton-container {
    width: 100%;
    grid-template-columns: 1fr;
    gap: 14px;
    padding: 10px 0 90px;
    box-sizing: border-box;
  }

  .skeleton-container > * {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

}

.pagination-skeleton-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-row-gap: 20px;
  justify-content: center;
  padding-bottom: 20px;
}

@media (max-width: 430px) {
  .pagination-skeleton-row {
    grid-template-columns: 1fr;
    gap: 14px;
    padding: 10px 0 90px;
  }
}

.add-button:disabled {
  cursor: not-allowed;

  opacity: 0.28;

  pointer-events: auto;

  transform: none;
}

.add-button:disabled:hover {
  opacity: 0.28;

  transform: none;
}

.add-button:disabled img {
  opacity: 0.7;
}

.create-vacancy-control {
  position: relative;

  display: flex;
  align-items: center;
}

.create-vacancy-control.disabled {
  cursor: not-allowed;
}


/* disabled button */

.add-button:disabled {
  opacity: 0.28;

  cursor: not-allowed;

  transform: none;
}

.add-button:disabled:hover {
  opacity: 0.28;

  transform: none;
}

.add-button:disabled img {
  filter: grayscale(1);

  opacity: 0.7;
}


/* hint */

.create-vacancy-hint {
  position: absolute;

  right: 0;
  bottom: calc(100% + 12px);

  width: 245px;

  box-sizing: border-box;

  padding:
    11px
    13px;

  opacity: 0;

  visibility: hidden;

  pointer-events: none;

  color:
    #424a55;

  background:
    #f6f8fc;

  border:
    1px solid
    rgba(55, 127, 228, 0.12);

  border-radius:
    13px;

  box-shadow:
    0
    10px
    26px
    rgba(15, 23, 42, 0.08);

  transform:
    translateY(5px);

  transition:
    opacity .18s ease,
    transform .18s ease,
    visibility .18s ease;

  z-index: 20;
}


/* triangle */

.create-vacancy-hint::before {
  content: "";

  position: absolute;

  right: 18px;
  bottom: -8px;

  width: 0;
  height: 0;

  border-left:
    8px solid transparent;

  border-right:
    8px solid transparent;

  border-top:
    8px solid
    rgba(55, 127, 228, 0.12);
}


.create-vacancy-hint::after {
  content: "";

  position: absolute;

  right: 19px;
  bottom: -6px;

  width: 0;
  height: 0;

  border-left:
    7px solid transparent;

  border-right:
    7px solid transparent;

  border-top:
    7px solid #f6f8fc;
}


/* show */

.create-vacancy-control.disabled:hover
.create-vacancy-hint {
  opacity: 1;

  visibility: visible;

  transform:
    translateY(0);
}


/* text */

.create-vacancy-hint-title {
  margin-bottom:
    4px;

  color:
    #3f5877;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    10px;

  line-height:
    1.3;
}


.create-vacancy-hint-text {
  color:
    #858c95;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;

  line-height:
    1.45;
}


.create-vacancy-hint-text strong {
  color:
    #597ca7;

  font-family:
    "Eastman-Medium",
    sans-serif;
}


/* DARK */

html[data-theme="dark"]
.create-vacancy-hint {
  color:
    #e5e8ec;

  background:
    #22252b;

  border-color:
    rgba(110, 155, 215, 0.12);

  box-shadow:
    0
    12px
    28px
    rgba(0, 0, 0, 0.22);
}


html[data-theme="dark"]
.create-vacancy-hint::before {
  border-top-color:
    rgba(110, 155, 215, 0.12);
}


html[data-theme="dark"]
.create-vacancy-hint::after {
  border-top-color:
    #22252b;
}


html[data-theme="dark"]
.create-vacancy-hint-title {
  color:
    #b9cdea;
}


html[data-theme="dark"]
.create-vacancy-hint-text {
  color:
    #838b95;
}


html[data-theme="dark"]
.create-vacancy-hint-text strong {
  color:
    #91b6ee;
}

</style>